import { cn } from '@/lib/utils';

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  onStepClick?: (step: number) => void;
  disabled?: boolean;
  stepNames?: string[];
}

const defaultStepNames = [
  'Basic Details',
  'Upload Gcode',
  'Assign Tools to Gcode',
  'CAD/Project File Upload',
  'Job Setup Details',
  'License Details',
  'Post Status',
];

function ProgressBar({
  currentStep,
  totalSteps,
  onStepClick,
  disabled = false,
  stepNames = defaultStepNames,
}: ProgressBarProps) {
  const currentStepName = stepNames[currentStep - 1] || `Step ${currentStep}`;

  return (
    <div className="w-full bg-white border-b border-gray-200">
      {/* Mobile: compact circles + current step name */}
      <div className="flex sm:hidden flex-col px-4 py-2 gap-1">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSteps }, (_, index) => {
            const stepNumber = index + 1;
            const isCompleted = currentStep > stepNumber;
            const isCurrent = currentStep === stepNumber;

            return (
              <button
                key={stepNumber}
                type="button"
                aria-label={`Go to step ${stepNumber}`}
                onClick={() => onStepClick?.(stepNumber)}
                disabled={disabled}
                className="flex-1 flex items-center gap-1"
              >
                <div
                  className={cn(
                    'w-full h-1.5 rounded-full transition-colors duration-200',
                    isCompleted && 'bg-green-500',
                    isCurrent && 'bg-blue-700',
                    !isCompleted && !isCurrent && 'bg-gray-200'
                  )}
                />
              </button>
            );
          })}
        </div>
        <p className="text-xs text-blue-700 font-medium">
          Step {currentStep} of {totalSteps}: {currentStepName}
        </p>
      </div>

      {/* Desktop: full step list with names */}
      <div className="hidden sm:flex items-center justify-center space-x-2 px-4">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isCompleted = currentStep > stepNumber;
          const isCurrent = currentStep === stepNumber;
          const isPending = currentStep < stepNumber;
          const stepName = stepNames[index] || `Step ${stepNumber}`;

          return (
            <div key={stepNumber} className="flex items-center">
              <button
                type="button"
                className={cn(
                  'flex items-center space-x-2 px-2 py-3 relative',
                  isCurrent && 'bg-gray-100'
                )}
                onClick={() => onStepClick?.(stepNumber)}
                disabled={disabled}
              >
                <div
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors duration-200',
                    isCompleted && 'bg-green-500 text-white',
                    isCurrent && 'bg-blue-700 text-white',
                    isPending && 'bg-gray-300 text-gray-700'
                  )}
                >
                  {stepNumber}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium transition-colors duration-200',
                    isCurrent && 'text-blue-700',
                    !isCurrent && 'text-gray-700'
                  )}
                >
                  {stepName}
                </span>
                {isCurrent && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-700" />
                )}
              </button>
              {stepNumber < totalSteps && (
                <div className="mx-2 text-gray-400">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProgressBar;
